import { List } from "immutable";
import { RootState, RootActions } from "common/reducers";
import { ProductRecord, getEffectiveDiscountable, getEffectiveMarkupable } from "common/records/Product";
import { EstimateGroupRecord } from "common/records/EstimateGroup";
import { calculateFormula, getUoM, MeasurementRecord } from "common/records/Measurement";
import { calculateRoomFormula, RoomRecord } from "common/records/Room";
import { convert, InvalidConversionError } from "common/services/conversion.service";
import { productMeasurementLink } from "common/selectors/measurementLink.selectors";
import { currentMeasurement } from "common/selectors/measurement.common.selectors";
import { currentEstimateActivatedPriceListId } from "common/selectors/estimate.selectors";
import { Nullable } from "common/types";
import { productOptionsByIds, productOverviewById } from "common/selectors/product.selectors";
import { productOptionById } from "common/selectors/productOption.selectors";
import { productOptionGroupById } from "common/selectors/productOptionGroup.selectors";
import { productToOptionGroupLink } from "common/selectors/productToOptionGroupLink.selectors";
import { relativePathProductAncestorDocumentsInSnapshot } from "common/selectors/document.selectors";
import { imageUrlsFromIds } from "common/selectors/image.selectors";
import { EstimateEditorActions } from "common/reducers/estimateEditor.actions";
import { estimateLineItem as estimateLineItemSelector } from "common/selectors/estimateLineItem.selectors";
import { estimateLineItemOption as estimateLineItemOptionSelector } from "common/selectors/estimateLineItemOption.selectors";
import { openingEstimationMeasurement } from "common/records/EstimateLineItem";
import { OpeningEstimationRecord } from "common/records/OpeningEstimation";
import type { OpeningRecord } from "common/records/Opening";
import { EstimateLineItemOptionRecord } from "common/records/EstimateLineItemOption";
import { updateNodeAndParents } from "common/helpers/EstimateTree";
import round from "lodash/round";

/**
 * Converts a measurement value into the product's UOM, falling back to the raw value on an
 * unsupported pair so the product copy never aborts (matches rsf-app).
 */
const safeConvert = (value: number, fromUom: string, product: ProductRecord): number => {
  if (!fromUom || fromUom === product.uom) return value;
  try {
    return convert(value, fromUom, product.uom, product.standard_measurement, product.standard_uom);
  } catch (error) {
    // Swallow only the unsupported-pair error; rethrow anything else so real bugs aren't masked.
    if (!(error instanceof InvalidConversionError)) throw error;
    return value;
  }
};

/**
 * Derives the quantity a product seeds when selected, from the org's measurement link
 * (via `productMeasurementLink`). Ports rsf-app `EstimatorService.addMeasurementLink`, one branch
 * per link kind: `measurement`/`formula` read+UOM-convert the measurement field/formula;
 * `interior_measurement`/`interior_formula` sum that over the group's rooms (falling back to all
 * measurement rooms). Returns `currentQuantity` when there's no link or no current measurement.
 *
 * @param {RootState} state The RootState
 * @param {ProductRecord} product The product being applied to the line item
 * @param {Nullable<EstimateGroupRecord>} estimateGroup The line item's group (for interior kinds)
 * @param {number} currentQuantity The line item's existing quantity (the fallback)
 * @returns {number} the derived quantity
 */
export const deriveLineItemQuantity = (
  state: RootState,
  product: ProductRecord,
  estimateGroup: Nullable<EstimateGroupRecord>,
  currentQuantity: number,
): number => {
  const link = productMeasurementLink(state, { product });
  if (!link) return currentQuantity;

  const measurement = currentMeasurement(state) as Nullable<MeasurementRecord>;
  if (!measurement) return currentQuantity;

  const uomMap = getUoM(measurement) as Record<string, string>;

  switch (link.kind) {
    case "measurement": {
      const field = link.measure_value.measurement;
      const raw = (measurement as any)[field];
      if (raw === undefined || raw === null) {
        // rsf-app: when the field is a numeric literal rather than a measurement key, use it.
        const parsed = parseFloat(field);
        return Number.isNaN(parsed) ? currentQuantity : parsed;
      }
      return safeConvert(raw, uomMap[field], product);
    }
    case "formula": {
      const { formula, formula_uom } = link.measure_value;
      const { result, error } = calculateFormula(formula, measurement);
      if (error || result === null || result === undefined) return currentQuantity;
      return safeConvert(result, formula_uom, product);
    }
    case "interior_measurement": {
      const rooms = roomsForGroup(state, measurement, estimateGroup);
      if (rooms.size === 0) return currentQuantity;
      const field = link.measure_value.measurement;
      let quantity = 0;
      rooms.forEach((r) => {
        const value = (r as any)?.[field];
        if (value !== undefined && value !== null) quantity += round(value, 2);
      });
      return round(quantity, 2);
    }
    case "interior_formula": {
      const rooms = roomsForGroup(state, measurement, estimateGroup);
      if (rooms.size === 0) return currentQuantity;
      const { formula, formula_uom } = link.measure_value;
      let quantity = 0;
      rooms.forEach((r) => {
        if (!r) return;
        const { result, error } = calculateRoomFormula(formula, r);
        if (error || result === null || result === undefined) return;
        quantity += safeConvert(result, formula_uom, product);
      });
      return round(quantity, 2);
    }
    default:
      return currentQuantity;
  }
};

/**
 * Rooms an interior link sums over: the group's `room_estimations` rooms, else all measurement
 * rooms (matches rsf-app's fallback).
 */
const roomsForGroup = (
  state: RootState,
  measurement: MeasurementRecord,
  estimateGroup: Nullable<EstimateGroupRecord>,
): List<Nullable<RoomRecord>> => {
  let roomIds = estimateGroup ? List(estimateGroup.room_estimations.map((re) => re.room_id)) : List<number>();
  if (roomIds.size === 0) roomIds = measurement.room_ids;
  // Flat store lookup (matches `room.selectors.room`); importing room.selectors here forms a
  // circular-init cycle through the reducer import graph.
  return roomIds.map((id) => state.getIn(["rooms", "byId", id]) as Nullable<RoomRecord>);
};

/**
 * A product option to seed on a line item when a product is applied: the resolved option id, the
 * snapshot group the link points at (not the option's own `option_group_id`, which can be stale),
 * and the default quantity declared on the link.
 */
export interface DefaultOptionToAdd {
  productOptionId: number;
  productOptionGroupId: number;
  quantity: number;
}

/**
 * Default product options to add when a product is applied. Ports rsf-app
 * `EstimatorService.addDefaultOptions`: for each `ptog_link_id` declaring `options.default`,
 * resolve each `{ uuid: qty }` to a ProductOption (by uuid in the link's group) and emit it.
 * Resolves links/groups/options from the global byId stores. Returns only WHICH options to add;
 * the reducer's ADD_ESTIMATE_LINE_ITEM_OPTION path builds the records.
 *
 * @param {RootState} state The RootState
 * @param {ProductRecord} product The product being applied
 * @returns {DefaultOptionToAdd[]} the options to seed, in link/uuid declaration order
 */
export const defaultOptionsForProduct = (state: RootState, product: ProductRecord): DefaultOptionToAdd[] => {
  const result: DefaultOptionToAdd[] = [];
  const aplId = currentEstimateActivatedPriceListId(state);
  if (!aplId) return result;

  product.ptog_link_ids.forEach((linkId) => {
    const link = productToOptionGroupLink(state, linkId);
    if (!link) return;

    const defaults = link.options?.get?.("default");
    if (!defaults) return;

    const group = productOptionGroupById(state, link.option_group_id);
    if (!group) return;

    const options = productOptionsByIds(state, { productOptionIds: group.option_ids });

    defaults.forEach((qty: number, optUuid: string) => {
      const option = options.find((opt) => opt.uuid === optUuid);
      if (!option) return;
      result.push({ productOptionId: option.id, productOptionGroupId: group.id, quantity: qty });
    });
  });

  return result;
};

/**
 * Whether a variant swap should prompt before overwriting an edited value — rsf-app
 * `ProductColorDropdown` parity:
 * - `descriptionEdited`: the current description matches NEITHER the old product's NOR the new
 *   product's description (the user typed their own), so replacing it would lose their edit.
 * - `quantityEdited`: the OLD product's measurement-link quantity differs from the current quantity
 *   (the user overrode the auto-derived value), so recomputing for the new product would lose it.
 * Resolves the group from the flat store (importing estimateGroup.selectors here would form a
 * circular-init cycle through the reducer import graph — same rationale as `roomsForGroup`).
 *
 * @param {RootState} state The RootState
 * @param {number} lineItemId The line item being swapped
 * @param {number} newProductId The product being swapped in
 * @returns {{ descriptionEdited: boolean; quantityEdited: boolean }} which confirm prompts to show
 */
export const lineItemSwapPrompts = (
  state: RootState,
  lineItemId: number,
  newProductId: number,
): { descriptionEdited: boolean; quantityEdited: boolean } => {
  const lineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
  if (!lineItem) return { descriptionEdited: false, quantityEdited: false };

  const oldProduct = lineItem.product_id ? productOverviewById(state, lineItem.product_id) : null;
  const newProduct = productOverviewById(state, newProductId);

  const description = lineItem.description ?? "";
  const descriptionEdited =
    description !== (oldProduct?.description ?? "") && description !== (newProduct?.description ?? "");

  const group = state.getIn(["estimateGroups", "byId", lineItem.estimate_group_id]) as Nullable<EstimateGroupRecord>;
  const currentQuantity = lineItem.quantity ?? 0;
  const oldDerivedQuantity = oldProduct
    ? deriveLineItemQuantity(state, oldProduct, group, currentQuantity)
    : currentQuantity;
  const quantityEdited = oldDerivedQuantity !== currentQuantity;

  return { descriptionEdited, quantityEdited };
};

/**
 * Line-item fields seeded from a product (rsf-app EstimateLineItem.setProduct).
 * Discountable/markupable go through getEffectiveDiscountable/getEffectiveMarkupable to collapse
 * parent inheritance — occ-app's isDiscountable/isMarkupable treat raw "default" as yes, so copying
 * an unresolved "default" would lose a parent's inherited "no" and over-discount/over-markup.
 *
 * @param {RootState} state The RootState
 * @param {ProductRecord} product The product being applied
 * @returns {Record<string, unknown>} the line-item field overrides
 */
export const productLineItemFields = (state: RootState, product: ProductRecord): Record<string, unknown> => {
  // Snapshot products carry image_ids, not hydrated records; resolve URLs from the global image
  // store (rsf-app's `i.file?.url` → the ImageRecord's `file.url`).
  const productImageUrls = imageUrlsFromIds(state, product.image_ids);
  return {
    product_id: product.id,
    product_uuid: product.uuid,
    name: product.ancestral_name,
    description: product.description,
    price: product.price,
    base_product_price: product.product_price || 0,
    base_labor_price: product.labor_price || 0,
    cost: product.cost,
    msrp: product.msrp,
    visibility: product.visibility,
    uom: product.uom,
    discountable: getEffectiveDiscountable(product),
    markupable: getEffectiveMarkupable(product),
    product_image_urls: productImageUrls,
    image_ids: List<number>(),
    document_ids: List<number>(),
    estimate_doc_urls: relativePathProductAncestorDocumentsInSnapshot(state, {
      productId: product.id,
      display: "estimate",
    }),
    contract_doc_urls: relativePathProductAncestorDocumentsInSnapshot(state, {
      productId: product.id,
      display: "contract",
    }),
  };
};

/**
 * Replaces a line item's options with the product's defaults (rsf-app replace semantics): clears
 * existing options first, then adds each default and stamps its declared quantity. Takes the
 * estimate-editor `reducer` as a param to replay that action without a circular import.
 *
 * @param {RootState} state The RootState
 * @param {number} lineItemId The line item to seed defaults onto
 * @param {ProductRecord} product The product whose defaults to apply
 * @param {(state: RootState, action: RootActions) => RootState} reducer The estimate-editor reducer
 * @returns {RootState} the updated state
 */
export const applyDefaultOptions = (
  state: RootState,
  lineItemId: number,
  product: ProductRecord,
  reducer: (state: RootState, action: RootActions) => RootState,
): RootState => applyDefaultOptionsFromList(state, lineItemId, defaultOptionsForProduct(state, product), reducer);

/**
 * Replaces a line item's options with a resolved option set (rsf-app replace semantics): clears
 * existing options first, then adds each option and stamps its declared quantity (opening-scaled for
 * per_ui/per_sqft). Shared by the product-apply path (defaults come from the product) and the
 * estimate-defaults Use path (defaults come from the estimate, resolved to this product by match_id).
 *
 * @param {RootState} state The RootState
 * @param {number} lineItemId The line item to seed options onto
 * @param {DefaultOptionToAdd[]} optionsToAdd The resolved options to add, in order
 * @param {(state: RootState, action: RootActions) => RootState} reducer The estimate-editor reducer
 * @returns {RootState} the updated state
 */
export const applyDefaultOptionsFromList = (
  state: RootState,
  lineItemId: number,
  optionsToAdd: DefaultOptionToAdd[],
  reducer: (state: RootState, action: RootActions) => RootState,
): RootState => {
  // Clear existing options before seeding the new defaults. Persisted options (id > 0) are marked
  // `_destroy: true` and KEPT on `option_ids` so the save serializer emits `{ id, _destroy: true }`
  // and the server deletes them; hard-deleting would orphan the row server-side. Unsaved options
  // (negative ids) never reached the server, so they're hard-deleted.
  const existing = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
  if (existing && existing.option_ids.size > 0) {
    const persistedOptionIds = existing.option_ids.filter((optionId) => optionId > 0);
    existing.option_ids
      .filter((optionId) => optionId <= 0)
      .forEach((optionId) => {
        state = state.deleteIn(["estimateLineItemOptions", "byId", optionId]);
      });
    persistedOptionIds.forEach((optionId) => {
      state = state.setIn(["estimateLineItemOptions", "byId", optionId, "_destroy"], true);
    });
    state = state.setIn(["estimateLineItems", "byId", lineItemId, "option_ids"], persistedOptionIds);
  }

  optionsToAdd.forEach(({ productOptionId, productOptionGroupId, quantity }) => {
    state = reducer(
      state,
      EstimateEditorActions.addEstimateLineItemOption(lineItemId, productOptionId, productOptionGroupId),
    );
    const lineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
    const newOptionId = lineItem?.option_ids.last(undefined);
    if (lineItem && newOptionId !== undefined && quantity !== undefined) {
      // Opening-backed line items scale per_ui/per_sqft option quantities by the opening's
      // UI/area at add time (rsf-app addDefaultOptions); without openings this is the declared
      // quantity unchanged.
      const newOption = estimateLineItemOptionSelector(state, { estimateLineItemOptionId: newOptionId });
      // Resolve the first live opening at use from the normalized slice by the line item's
      // `opening_estimation_ids`; inline (not via the aggregate opening selector) to avoid a
      // module-init cycle through measurement.common.selectors.
      const firstOe = (lineItem.opening_estimation_ids ?? List<number>())
        .map((oeId) => state.getIn(["openingEstimations", "byId", oeId]) as OpeningEstimationRecord | undefined)
        .find((oe): oe is OpeningEstimationRecord => !!oe && !oe._destroy);
      let opening: OpeningRecord | null = firstOe?.openable ?? null;
      if (firstOe?.openable_type && firstOe.openable_id) {
        opening =
          (state.getIn([`${firstOe.openable_type.toLowerCase()}s`, "byId", firstOe.openable_id]) as
            | OpeningRecord
            | undefined) ??
          firstOe.openable ??
          null;
      }
      const scaledQuantity = newOption
        ? openingEstimationMeasurement(opening, newOption.charge_type, quantity)
        : quantity;
      state = state.setIn(["estimateLineItemOptions", "byId", newOptionId, "quantity"], scaledQuantity);
      state = updateNodeAndParents(state, "option", newOptionId);
    }
  });
  return state;
};

/**
 * A line-item option carried over to a new product on a product switch: the new product's
 * equivalent option/group ids with the existing option's quantity preserved.
 */
export interface IMatchedLineItemOption {
  productOptionId: number;
  productOptionGroupId: number;
  quantity: number;
}

/**
 * Compares a line item's current options against a new product's option groups for the
 * product-switch confirm (rsf-app EstimateLineItem.compareOptions): an option carries over when
 * the new product has a group with the same `match_id` containing an option with the same
 * `match_id`; everything else is rejected as a "Group: Option" label. Matching is by the elio's
 * resolved ProductOption `match_id` (falling back to the stored `product_option_match_id`).
 *
 * @param {RootState} state The RootState
 * @param {number} lineItemId The line item whose options to compare
 * @param {ProductRecord} newProduct The product being switched to
 * @returns {{ matched: IMatchedLineItemOption[]; rejected: string[] }}
 */
export const compareLineItemOptions = (
  state: RootState,
  lineItemId: number,
  newProduct: ProductRecord,
): { matched: IMatchedLineItemOption[]; rejected: string[] } => {
  const matched: IMatchedLineItemOption[] = [];
  const rejected: string[] = [];

  const lineItem = estimateLineItemSelector(state, { estimateLineItemId: lineItemId });
  if (!lineItem) {
    return { matched, rejected };
  }

  const newGroups = newProduct.option_group_ids
    .map((id) => productOptionGroupById(state, id))
    .filter((group): group is NonNullable<typeof group> => Boolean(group));

  lineItem.option_ids.forEach((optionId) => {
    const elio: Nullable<EstimateLineItemOptionRecord> = estimateLineItemOptionSelector(state, {
      estimateLineItemOptionId: optionId,
    });
    if (!elio || elio._destroy) return;

    const elioGroup = productOptionGroupById(state, elio.product_option_group_id);
    const elioOption = elio.product_option_id ? productOptionById(state, elio.product_option_id) : null;
    const elioOptionMatchId = elioOption?.match_id || elio.product_option_match_id;
    const label = `${elioGroup?.name ?? elio.product_option_group_name}: ${elioOption?.name ?? elio.name}`;

    const newGroup = elioGroup ? newGroups.find((group) => group.match_id === elioGroup.match_id) : undefined;
    if (!newGroup) {
      rejected.push(label);
      return;
    }

    const newOption = elioOptionMatchId
      ? productOptionsByIds(state, { productOptionIds: newGroup.option_ids }).find(
          (opt) => opt.match_id === elioOptionMatchId,
        )
      : undefined;
    if (!newOption) {
      rejected.push(label);
      return;
    }

    matched.push({ productOptionId: newOption.id, productOptionGroupId: newGroup.id, quantity: elio.quantity });
  });

  return { matched, rejected };
};
